import { supabase } from './supabase.js';

const BUCKET = 'listing-photos';

/**
 * Comprime una imagen antes de subirla.
 * Resultado: máximo 800x800px, WebP al 82%, ~50-150KB (ahorro ~70% vs original).
 * @param {File} file
 * @returns {Promise<File>}
 */
export function compressImageWithOptions(file, { maxPx = 800, quality = 0.82 } = {}) {
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX = maxPx;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        if (width > height) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        } else {
          width = Math.round((width * MAX) / height);
          height = MAX;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => {
          resolve(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' }) : file);
        },
        'image/webp',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

export async function compressImage(file) {
  return compressImageWithOptions(file, { maxPx: 800, quality: 0.82 });
}

/** Más resolución para OCR en escaneo IA (texto pequeño en portada/lomo). También usa WebP para ahorrar storage. */
export async function compressImageForAiScan(file) {
  return compressImageWithOptions(file, { maxPx: 1800, quality: 0.92 });
}

/** Carteles / fotos Comunidad: legibles en feed sin pesar la BD. GIF se conserva (animación). */
export async function compressImageForComunidad(file) {
  if (file?.type === 'image/gif') return file;
  return compressImageWithOptions(file, { maxPx: 1400, quality: 0.85 });
}

/** Portadas noticias: WebP ~1400px, calidad 85 % (GIF/MP4 sin tocar). */
export async function compressImageForNoticias(file) {
  if (file?.type === 'image/gif' || file?.type === 'video/mp4') return file;
  return compressImageWithOptions(file, { maxPx: 1400, quality: 0.85 });
}

/**
 * Sube una foto de listing a Supabase Storage.
 * Ruta: listing-photos/{userId}/{listingId|tmp_…}/{timestamp}.{ext}
 * (La primera carpeta debe ser el UUID del vendedor — política RLS.)
 *
 * @param {File} file - archivo de imagen
 * @param {string} userId - UUID del vendedor
 * @param {string|null} [listingId] - UUID del listing; si no hay aún, se usa carpeta temporal
 * @returns {{ url: string|null, error: string|null }}
 */
export async function uploadListingPhoto(file, userId, listingId = null) {
  if (!file || !userId) {
    return { url: null, error: 'missing_file_or_user' };
  }

  const compressed = await compressImage(file);

  const rawExt =
    (compressed.name && compressed.name.includes('.') ? compressed.name.split('.').pop() : '') || 'jpg';
  const ext = /^[a-z0-9]+$/i.test(rawExt) ? rawExt.toLowerCase() : 'webp';
  const folder = listingId ?? `tmp_${Date.now()}`;
  const objectPath = `${userId}/${folder}/${Date.now()}.${ext}`;

  const { error } = await supabase.storage.from(BUCKET).upload(objectPath, compressed, {
    cacheControl: '3600',
    upsert: false,
  });

  if (error) {
    console.error('[uploadListingPhoto] error:', error.message);
    return { url: null, error: error.message };
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(objectPath);

  return { url: data.publicUrl, error: null };
}
